import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { handleSendEventRequest, throwSuccess } from 'store/slices';
import { getUserType, objectToFormData } from 'utils/helpers';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Card from 'components/Layout/Card';
import EventAcceptanceLetterheadForm from 'components/ReusableForms/EventAcceptanceLetterheadForm';
// import NewRegistrationEventForm from '../../EventForm/NewRegistrationEventForm';
import ServicesDataForm from '../../EventForm/AddOnServices/ServicesDataForm';
import EventFormV2 from '../EventFormV2';
import EventProcessSideSection from '../EventProcessSideSection';
import Button from 'components/form/Button';
import CheckBox from 'components/form/CheckBox';
import RegistrationEventForm from '../../EventForm/RegistrationEventForm/RegistrationEventForm';

const ApprovalHub = ({ isViewMode, user_type, isEditMode }) => {
  const userType = getUserType();
  const tabOrder = [
    'events',
    'acceptance_letterhead',
    'registration',
    'add_on_service',
  ];
  const [activeTab, setActiveTab] = useState('events');
  const [completedTabs, setCompletedTabs] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);
  const isTemp = queryParams.get('istemp');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(true);

  // Check if a tab is disabled
  const isTabDisabled = (key) => {
    if (isViewMode || isTemp || isEditMode) return false;
    const currentIndex = tabOrder.indexOf(key);
    return (
      currentIndex > 0 && !completedTabs.includes(tabOrder[currentIndex - 1])
    );
  };

  // Handle moving to the next tab
  const handleNext = (currentKey) => {
    const currentIndex = tabOrder.indexOf(currentKey);
    const nextTab = tabOrder[currentIndex + 1];
    if (nextTab) {
      setCompletedTabs((prev) => [...prev, currentKey]);
      setActiveTab(nextTab);
    }
  };

  const handleSendEventRequestData = async () => {
    setIsLoading(true);
    const payload = objectToFormData({
      event_id: params?.eventId,
      ...(isTemp && { is_temp: true }),
      ...(isCheckboxChecked && {
        is_visible_to_third_party: isCheckboxChecked ? '1' : '0',
      }),
    });

    // Dispatch the action to send the request
    const response = await dispatch(handleSendEventRequest(payload));
    if (response.status === 200) {
      dispatch(throwSuccess(response?.message));
      setIsLoading(false);
      navigate(`/${userType}/raised-request/event-management`);
    }
    setIsLoading(false);
  };

  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));
  const { create: createAccess } =
    userDetails?.role_data?.find(
      (item) => item?.permission_name === 'Event Management'
    )?.sub_permission || {};

  return (
    <>
      <Card>
        {/* Tabs Navigation */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => {
            if (!isTabDisabled(k)) setActiveTab(k);
          }}
          className="custom-tabs mb-2 gap-4 cpt-15 cps-15"
        >
          <Tab
            eventKey="events"
            title="Add Events"
            disabled={isTabDisabled('events')}
          />
          <Tab
            eventKey="acceptance_letterhead"
            title="Acceptance Letterhead"
            disabled={isTabDisabled('acceptance_letterhead')}
          />

          <Tab
            eventKey="registration"
            title="Registration"
            disabled={isTabDisabled('registration')}
          />
          <Tab
            eventKey="add_on_service"
            title="Add-On Services"
            disabled={isTabDisabled('add_on_service')}
          />
        </Tabs>

        <div className="row">
          <div className="col-md-9">
            {activeTab === 'events' && (
              <EventFormV2
                handleNext={handleNext}
                isViewMode={isViewMode}
                isTemp={isTemp}
              />
            )}
            {activeTab === 'acceptance_letterhead' && (
              <>
                <div className="cmt-20 cms-20 cmb-20">
                  <EventAcceptanceLetterheadForm
                    eventId={params.eventId}
                    eventTab
                    handleNext={handleNext}
                    isTemp={isTemp}
                  />
                </div>
              </>
            )}

            {activeTab === 'registration' && (
              <div className="cmt-20 cms-20">
                {/* Old Form */}
                {/* <NewRegistrationEventForm
                  eventId={params.eventId}
                  eventTab
                  isModal
                  handleNext={handleNext}
                  isTemp={isTemp}
                /> */}
                <RegistrationEventForm
                  eventId={params.eventId}
                  eventTab
                  isModal
                  handleNext={handleNext}
                  isTemp={isTemp}
                />
              </div>
            )}
            {activeTab === 'add_on_service' && (
              <div className="cmt-20 cms-20">
                <ServicesDataForm
                  eventId={params.eventId}
                  eventTab
                  isModal
                  handleNext={handleNext}
                  isTemp={isTemp}
                />
              </div>
            )}
          </div>

          {/* Right side: Sidebar/Status */}
          <div
            className="col-md-3 cps-20 "
            style={{ borderLeft: '1px solid #ccc' }}
          >
            <EventProcessSideSection
              tabOrder={tabOrder}
              isViewMode={isViewMode}
              event_id={params.eventId}
              type={activeTab}
              isTemp={isTemp}
            />
          </div>
        </div>
      </Card>
      {['6'].includes(user_type) && !isViewMode && (
        <div>
          {createAccess === '1' && (
            <div className="d-flex align-items-center gap-2 cmt-18">
              <CheckBox
                type="PRIMARY-ACTIVE"
                onClick={() => {
                  setIsCheckboxChecked(!isCheckboxChecked);
                }}
                isChecked={isCheckboxChecked}
              />
              <span>
                I confirm this information should be reflected on both the event
                website and the IFERP Dashboard.
              </span>
            </div>
          )}

          <div className="d-flex gap-3 cpt-15">
            {createAccess === '1' && (
              <Button
                text="Send Request"
                btnStyle="primary-dark"
                className="h-37 text-14-500 cps-20 cpe-20"
                onClick={handleSendEventRequestData}
                disabled={isLoading}
                btnLoading={isLoading}
              />
            )}
            <Button
              text="Cancel"
              btnStyle="light-outline"
              className="h-37 text-14-500 cps-20 cpe-20"
              onClick={() => navigate(`/${userType}/event-management/events`)}
              disabled={isLoading}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovalHub;
